import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectsService } from '../../../../../core/projects.service';
import { AuthService, AppService } from '../../../../../../../shared/services';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';
import {
  WarrantyClaimActionRequest,
  FetchLineageRequest,
  FetchWarrantyClaimDetailRequest,
} from '../../../../../core/projects.actions';
import { UploadFileRequest } from '../../../../../../../shared/core/shared.actions';
import { warrantyClaimDetail, lineageList } from '../../../../../core/projects.selectors';
import { WarrantyClaimFormComponent } from './warranty-claim-form.component';
import { issueStatusText } from '../../../../../../../shared/helpers/issue-workflow';

@Component({
  selector: 'app-warranty-claim-detail',
  templateUrl: './warranty-claim-detail.component.html',
  styleUrls: ['./warranty-claim-detail.component.scss'],
  standalone: false,
})
export class WarrantyClaimDetailComponent implements OnInit, OnDestroy {
  /** A linked issue's status as its badge prints it (a contractor-workflow stage in words). */
  readonly issueStatusText = issueStatusText;

  @Input() data: any;
  claim: any;
  projectId: string;
  role: string;

  @ViewChild('actionFileInput', { static: false }) actionFileInputRef: ElementRef;
  selectedTabIndex = 0;
  actionForm: FormGroup;
  actionOptions: { value: string; label: string }[] = [];
  workflowHistory: any[] = [];
  actionMediaFiles: any[] = [];

  // Linked issues
  linkedIssues: any[] = [];
  linkedIssuesLoading = false;

  severityMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
    low: { label: 'Low', bgColor: 'bg-info', textColor: 'txt-info' },
    medium: { label: 'Medium', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    high: { label: 'High', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
    critical: { label: 'Critical', bgColor: 'bg-critical', textColor: 'txt-critical' },
  };

  statusMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
    draft: { label: 'Draft', bgColor: 'bg-draft', textColor: 'txt-draft' },
    sent_back: { label: 'Sent back to inspector', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
    submitted: { label: 'Submitted', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    pm_review: { label: 'PM Review', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    approved: { label: 'Approved', bgColor: 'bg-approved', textColor: 'txt-approved' },
    rejected: { label: 'Rejected', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
    sent_to_contractor: { label: 'Sent to contractor', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    contractor_notified: { label: 'Sent to contractor', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    in_progress: { label: 'In Progress', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    responded: { label: 'Contractor responded', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    verified: { label: 'Verified', bgColor: 'bg-approved', textColor: 'txt-approved' },
    disputed: { label: 'Disputed', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
    remediation_complete: { label: 'Remediation Complete', bgColor: 'bg-approved', textColor: 'txt-approved' },
    closed: { label: 'Closed', bgColor: 'bg-approved', textColor: 'txt-approved' },
  };

  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store,
    private projectsService: ProjectsService,
    private authService: AuthService,
    public appService: AppService,
    private commonService: CommonService,
    private appHttpService: AppHttpService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.actionForm = new FormGroup({
      action: new FormControl(''),
      notes: new FormControl(''),
    });
  }

  ngOnInit() {
    this.claim = this.data;

    // Extract projectId — right panel components may not have route params directly
    const url = this.router.url;
    const segments = url.split('?')[0].split('/');
    this.projectId = this.route.snapshot.paramMap.get('projectId')
      || segments[segments.indexOf('projects') + 1]
      || '';

    const account = this.authService.getCurrentUser();
    this.role = account?.roles?.find(r => r.project_id === this.projectId)?.role
      || account?.role
      || '';

    this.buildActionOptions();
    this.fetchLineage();

    // Fetch fresh claim data to ensure status is current
    if (this.claim?.id) {
      this.store.dispatch(FetchWarrantyClaimDetailRequest({ payload: { claimId: this.claim.id } }));
      this.fetchLinkedIssues(this.claim.id);
    }

    this.store.select(warrantyClaimDetail)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(detail => {
        if (detail && detail.id === this.claim?.id) {
          this.claim = { ...detail, ...(detail.attributes || {}) };
          this.buildActionOptions();
        }
      });

    // Subscribe to lineage data for Discussion tab
    this.store.select(lineageList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.length) {
          // Deep clone to avoid NgRx frozen object errors
          const cloned = JSON.parse(JSON.stringify(data));
          this.workflowHistory = cloned.map(step => ({
            action: step?.state?.comment || step?.state?.name || 'Action',
            actor_name: step?.actor ? `${step.actor.first_name} ${step.actor.last_name}` : 'Unknown',
            actor_initials: this.getInitials(step?.actor),
            date: step?.created_at,
            notes: step?.input?.formValues?.comments || step?.input?.description || null,
            attachments: step?.attachments || [],
          }));
        }
      });

    // Listen for uploaded files for action attachments
    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = JSON.parse(JSON.stringify(Array.isArray(data) ? data : [data]));
        const newFiles = items
          .filter(i => i?.fileType === 'picture' || i?.fileType === 'document')
          .map(f => ({ ...f, tags: ['actions-attachments'] }));
        if (newFiles.length) {
          this.actionMediaFiles = [...this.actionMediaFiles, ...newFiles];
          setTimeout(() => this.projectsService.mediaData.next(this.actionMediaFiles));
        }
      });
  }

  onActionFileSelected(event: any) {
    const files: FileList = event.target.files;
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (file.type.startsWith('image/') || file.type.startsWith('application/') || file.type.startsWith('text/')) {
        formData.append('inputFiles', file);
      }
    }
    if (formData.has('inputFiles')) {
      this.store.dispatch(UploadFileRequest({ payload: formData }));
    }
    this.actionFileInputRef.nativeElement.value = '';
  }

  deleteActionPicture(pic: any) {
    this.actionMediaFiles = this.actionMediaFiles.filter(p => p.id !== pic.id);
    setTimeout(() => this.projectsService.mediaData.next(this.actionMediaFiles));
  }

  fetchLineage() {
    if (!this.claim?.id) return;
    this.store.dispatch(FetchLineageRequest({
      payload: {
        qp: {
          start: 0,
          total: 1000,
          ex: `warranty-claim/${this.claim.id}`,
          include: ['actor', 'state', 'attachments'],
        },
      },
    }));
  }

  /**
   * Build dropdown options based on claim status + user role.
   */
  buildActionOptions() {
    this.actionOptions = [];
    if (!this.claim) return;

    const status = this.claim.status;

    if (this.isPM()) {
      if (['submitted', 'responded', 'sent_to_contractor', 'verified'].includes(status)) {
        this.actionOptions = [
          { value: 'send_to_contractor', label: 'Send to contractor' },
          { value: 'close', label: 'Close' },
        ];
      }
      if (['submitted', 'sent_to_contractor', 'responded'].includes(status)) {
        // PM can verify as long as the claim hasn't been verified/closed yet
        this.actionOptions.splice(1, 0, { value: 'verify', label: 'Verify' });
      }
      if (status === 'submitted') {
        // Only show "Send back to inspector" if creator is NOT a PM/org-admin
        const creatorRole = this.claim?.createdBy?.role || '';
        const creatorIsPM = creatorRole === 'project-manager' || creatorRole === 'org-admin';
        if (!creatorIsPM) {
          this.actionOptions.push({ value: 'send_back', label: 'Send back to inspector' });
        }
      }
      if (status === 'closed') {
        this.actionOptions = [
          { value: 'reopen', label: 'Reopen' },
        ];
      }
    } else if (this.isInspector()) {
      // Inspector can verify any time after submission — contractor response is optional.
      if (['submitted', 'sent_to_contractor', 'responded'].includes(status)) {
        this.actionOptions = [
          { value: 'verify', label: 'Verify' },
        ];
      }
    }

    if (this.isWarrantyContractor()) {
      if (['sent_to_contractor', 'contractor_notified'].includes(status)) {
        this.actionOptions = [
          { value: 'respond', label: 'Respond' },
        ];
      }
      if (status === 'in_progress') {
        this.actionOptions = [
          { value: 'complete', label: 'Mark complete' },
        ];
      }
    }

    // Reset the action form when options change
    this.actionForm.get('action')?.setValue('');
    this.actionForm.get('notes')?.setValue('');
  }

  /**
   * Build workflow timeline from the claim's workflow_history JSONB array.
   */
  /**
   * Execute the selected workflow action.
   */
  executeAction() {
    const action = this.actionForm.get('action')?.value;
    if (!action || !this.claim) return;

    const notes = this.actionForm.get('notes')?.value || '';
    let apiAction = action;
    let data: any = { notes };

    switch (action) {
      case 'send_to_contractor':
        apiAction = 'approve';
        data = { notes };
        break;
      case 'verify':
        apiAction = 'verify';
        data = { notes };
        break;
      case 'close':
        apiAction = 'close';
        data = { notes };
        break;
      case 'respond':
        apiAction = 'respond';
        data = { notes };
        break;
      case 'send_back':
        apiAction = 'send-back';
        data = { notes };
        break;
      case 'reopen':
        apiAction = 'reopen';
        data = { notes };
        break;
      case 'approve':
        apiAction = 'approve';
        data = { notes };
        break;
      case 'reject':
        apiAction = 'reject';
        data = { notes: notes || 'Rejected' };
        break;
    }

    // Include action attachments
    if (this.actionMediaFiles.length) {
      data.pictures = this.actionMediaFiles.map(f => f.id);
    }

    this.store.dispatch(WarrantyClaimActionRequest({
      payload: {
        action: apiAction,
        claimId: this.claim.id,
        projectId: this.projectId,
        warrantyId: this.claim.warranty?.id || this.claim.warranty_id,
        data,
      },
    }));

    // Immediately clear actions to prevent double-submit, reset attachments
    this.actionOptions = [];
    this.actionForm.get('action')?.setValue('');
    this.actionForm.get('notes')?.setValue('');
    this.actionMediaFiles = [];
    setTimeout(() => this.fetchLineage(), 1000);
  }

  getInitials(user: any): string {
    if (!user) return '?';
    return (user.first_name?.[0] || '') + (user.last_name?.[0] || '');
  }

  isPM(): boolean {
    return this.role === 'project-manager' || this.role === 'org-admin';
  }

  isInspector(): boolean {
    return ['inspector', 'lead-inspector', 'contractor-inspector'].includes(this.role);
  }

  isContractor(): boolean {
    return this.role?.startsWith('contractor');
  }

  isWarrantyContractor(): boolean {
    return this.role?.startsWith('contractor');
  }

  /**
   * Who may open the claim in the edit form.
   *
   * The author keeps editing while the claim is still being drafted
   * (draft / sent_back). Beyond that the claim is in flight, and only a
   * PM/org-admin may correct it -- right up until it is closed. Closing is the
   * end of the line; a closed claim must be reopened before it can be edited.
   *
   * Editing never changes the claim's status: the workflow actions
   * (Verify / Send to contractor / Close / ...) remain the only way to move it.
   */
  canEditClaim(): boolean {
    const status = this.claim?.status;
    if (!status) return false;
    if (['draft', 'sent_back'].includes(status)) return true;
    return this.isPM() && status !== 'closed';
  }

  editClaim() {
    this.projectsService.closeRightPanel.next();
    setTimeout(() => {
      this.projectsService.openRightPanel.next({
        component: WarrantyClaimFormComponent,
        data: {
          warrantyId: this.claim.warranty?.id || this.claim.warranty_id,
          projectId: this.projectId,
          editClaim: this.claim,
          // The form builds its WARRANTY dropdown options from `warranties`.
          // We only ever edit a claim in place, so the claim's own warranty is
          // the only valid option -- without it the dropdown renders empty and
          // the required field looks unset.
          warranties: this.claim.warranty ? [this.claim.warranty] : [],
        },
      });
    }, 100);
  }

  close() {
    this.projectsService.closeRightPanel.next();
  }

  fetchLinkedIssues(claimId: number) {
    this.linkedIssuesLoading = true;
    this.appHttpService.getService(`/v1/warranty/claims/${claimId}/issues`).subscribe({
      next: (resp: any) => {
        this.linkedIssues = resp?.data || resp || [];
        this.linkedIssuesLoading = false;
      },
      error: () => {
        this.linkedIssues = [];
        this.linkedIssuesLoading = false;
      },
    });
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
